import type { ReactNode } from 'react'
import type { FilterFacet, FilterFacetValue } from '../../../state/filters'

/**
 * One multi-select facet: a labelled group of checkboxes.
 *
 * A `fieldset` with a `legend` is the multi-select here, rather than a listbox
 * with `aria-multiselectable`. Both are correct ARIA, but this one is native:
 * every checkbox is in the tab order, space toggles it, the group name is
 * announced on entry, and none of it depends on JavaScript keyboard handling
 * that has to be reimplemented for every screen reader.
 */

export interface FacetOption<V extends string> {
  readonly value: V
  readonly label: string
  /** Brand colour swatch, drawn next to the label. Operators only. */
  readonly color?: string
  /** Sites this option would match. Omitted while the map has published nothing. */
  readonly count?: number
}

interface FacetGroupProps<F extends FilterFacet> {
  readonly facet: F
  readonly legend: string
  readonly options: readonly FacetOption<FilterFacetValue<F>>[]
  readonly selected: readonly FilterFacetValue<F>[]
  readonly onToggle: (facet: F, value: FilterFacetValue<F>) => void
  /** Turns a count into the text a screen reader hears beside the label. */
  readonly countLabel: (count: number) => string
  /** Renders the swatch column even for options without a colour. */
  readonly className?: string
  /**
   * Rows the closed option list cannot hold, drawn under it inside the same
   * group - the operator facet's untracked networks are the only ones so far.
   * They belong to the same question the legend asks, so they belong inside the
   * fieldset that asks it rather than in a second group with a second heading.
   */
  readonly children?: ReactNode
}

export const FacetGroup = <F extends FilterFacet>({
  facet,
  legend,
  options,
  selected,
  onToggle,
  countLabel,
  className,
  children,
}: FacetGroupProps<F>) => (
  <fieldset className={`facet${className === undefined ? '' : ` ${className}`}`}>
    <legend className="facet__legend">{legend}</legend>

    <ul className="facet__list">
      {options.map((option) => (
        <li key={option.value} className="facet__row">
          <label className="facet__option">
            <input
              className="facet__checkbox"
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => onToggle(facet, option.value)}
            />

            {option.color === undefined ? null : (
              <span
                className="facet__swatch"
                style={{ backgroundColor: option.color }}
                aria-hidden="true"
              />
            )}

            <span className="facet__label">{option.label}</span>

            {option.count === undefined ? null : (
              <span className="facet__count">
                <span className="visually-hidden">{countLabel(option.count)}</span>
                <span aria-hidden="true">{option.count}</span>
              </span>
            )}
          </label>
        </li>
      ))}
    </ul>

    {children}
  </fieldset>
)
